import type { ReactNode } from "react";
import { Icon, type IconName, type IconTone } from "../icon";

const headingTags = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4",
  5: "h5",
  6: "h6",
} as const;

function EmptyStatePattern() {
  return (
    <svg
      data-slot="empty-state-pattern"
      aria-hidden="true"
      viewBox="0 0 336 336"
      fill="none"
      className="pointer-events-none absolute top-1/2 left-1/2 size-[336px] max-w-none -translate-x-1/2 -translate-y-1/2 text-outline-subtle"
    >
      <g stroke="currentColor">
        <circle cx="168" cy="168" r="167.5" strokeOpacity="0" />
        <circle cx="168" cy="168" r="143.5" strokeOpacity="0.04" />
        <circle cx="168" cy="168" r="119.5" strokeOpacity="0.24" />
        <circle cx="168" cy="168" r="95.5" strokeOpacity="0.48" />
        <circle cx="168" cy="168" r="71.5" strokeOpacity="0.67" />
        <circle cx="168" cy="168" r="47.5" strokeOpacity="0.88" />
      </g>
    </svg>
  );
}

export type EmptyStateHeadingLevel = keyof typeof headingTags;

export interface EmptyStateProps {
  /** Contextual decorative icon shown above the empty-state message. */
  icon: IconName;
  /** Primary message displayed as a semantic heading. */
  title: string;
  /** Optional explanation or next-step guidance displayed below the title. */
  description?: string;
  /** Optional consumer-provided action controls. */
  actions?: ReactNode;
  /** Semantic heading level. Defaults to h2. */
  headingLevel?: EmptyStateHeadingLevel;
  /** Compact presentation for bounded sections such as galleries. */
  variant?: "default" | "compact";
  /** Tone for the icon, used by compact feedback states. */
  iconTone?: IconTone;
}

/**
 * A page or section-level no-content state with a contextual icon, message,
 * optional supporting copy, and consumer-owned actions.
 */
function EmptyState({
  icon,
  title,
  description,
  actions,
  headingLevel = 2,
  variant = "default",
  iconTone,
}: EmptyStateProps) {
  const Heading = headingTags[headingLevel];

  return (
    <section
      data-slot="empty-state"
      className={
        variant === "compact"
          ? "relative flex aspect-square w-full flex-col items-center justify-center gap-(--spacing-12) overflow-hidden rounded-(--radius-sm) border border-solid border-outline-subtle bg-surface"
          : "relative mx-auto flex w-full max-w-[856px] flex-col items-center overflow-hidden bg-surface py-(--spacing-128)"
      }
    >
      {variant === "compact" ? (
        <div
          className={`flex size-(--spacing-40) items-center justify-center rounded-full ${
            iconTone === "negative"
              ? "bg-negative-container"
              : "bg-surface-container-low"
          }`}
        >
          <Icon name={icon} size="xs" tone={iconTone} aria-hidden />
        </div>
      ) : (
        <div className="relative flex size-[48px] items-center justify-center">
          <EmptyStatePattern />
          <div className="relative z-10">
            <Icon name={icon} size="md" aria-hidden />
          </div>
        </div>
      )}
      <div
        className={
          variant === "compact"
            ? "flex w-full flex-col items-center px-(--spacing-12) text-center"
            : "relative mt-(--spacing-20) flex w-full max-w-[500px] flex-col items-center px-(--spacing-32) text-center"
        }
      >
        <div className="flex w-full flex-col gap-(--spacing-6)">
          <Heading
            className={
              variant === "compact"
                ? "m-0 font-(family-name:--font-family-title) text-(length:--font-size-title-sm) font-(number:--font-weight-medium) leading-(--font-line-height-20) text-text-default"
                : "m-0 font-(family-name:--font-family-title) text-(length:--font-size-title-lg) font-(number:--font-weight-bold) leading-(--font-line-height-24) text-text-default"
            }
          >
            {title}
          </Heading>
          {description ? (
            <p
              className={
                variant === "compact"
                  ? "m-0 whitespace-pre-line font-(family-name:--font-family-title) text-(length:--font-size-body-small) font-(number:--font-weight-regular) leading-(--font-line-height-16) text-text-subtle"
                  : "m-0 font-(family-name:--font-family-title) text-(length:--font-size-title-sm) font-(number:--font-weight-medium) leading-(--font-line-height-20) text-text-subtle"
              }
            >
              {description}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div
            className={
              variant === "compact"
                ? "mt-(--spacing-20) flex flex-wrap justify-center gap-x-(--spacing-10) gap-y-(--spacing-10)"
                : "mt-(--spacing-32) flex flex-wrap justify-center gap-x-(--spacing-10) gap-y-(--spacing-10)"
            }
          >
            {actions}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export { EmptyState };
